import type { StackProps } from '@chakra-ui/react';
import { Box, FormLabel, HStack, Spinner, Switch } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useState,
  type Dispatch,
  type FunctionComponent,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SimAutoReloadState, useToggleAutoReloadMutation } from 'utils/graphql/hooks';
import type { Sim } from 'utils/graphql/hooks';
import { useIotSimToast } from 'utils/hooks/useIotSimToast';

interface AutoReloadToggleProps extends StackProps {
  simCards: Sim[];
  setRedirectUrl: Dispatch<SetStateAction<string | undefined>>;
}

export const AutoReloadToggle: FunctionComponent<AutoReloadToggleProps> = ({
  simCards,
  setRedirectUrl,
  ...rest
}) => {
  const { t } = useTranslation();
  const successToast = useIotSimToast({ status: 'success' });
  const warningToast = useIotSimToast({ status: 'warning' });
  const errorToast = useIotSimToast({ status: 'error' });
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState<boolean>(
    !simCards.some((sim) => sim.autoReloadState === SimAutoReloadState.Disabled),
  );
  const { mutateAsync, isPending, isError } = useToggleAutoReloadMutation({ retry: false });

  if (isError) {
    errorToast({
      title: t('error.generic'),
      description: t('error.raiseSupportRequest'),
    });
  }

  const handleToggle = useCallback(async () => {
    const result = await mutateAsync({
      enable: !enabled,
      iccids: simCards.map((sim) => sim.iccid),
    });

    const { redirectUrl, processedIccids, failedIccids } = result.toggleAutoReload;

    await queryClient.invalidateQueries({ queryKey: ['SimPage.infinite'] });
    setEnabled((prev) => !prev);

    if (redirectUrl) {
      setRedirectUrl(redirectUrl);

      return;
    }

    const hasProcessedIccids = processedIccids && processedIccids.length > 0;
    const hasFailedIccids = failedIccids && failedIccids.length > 0;

    const newStateTranslation = t(enabled ? 'common.disable' : 'common.enable');
    const processedTranslation = t('sim.autoReload.processedIccidsMessage', {
      newState: newStateTranslation,
      iccidList: processedIccids?.join(', '),
    });
    const failedTranslation = t('sim.autoReload.failedIccidsMessage', {
      newState: newStateTranslation,
      iccidList: failedIccids?.join(', '),
    });

    if (hasProcessedIccids && !hasFailedIccids) {
      successToast({
        title: t('sim.autoReload.succeedTitle'),
        description: processedTranslation,
      });
    } else if (hasProcessedIccids && hasFailedIccids) {
      warningToast({
        title: t('sim.autoReload.partiallyFailedTitle'),
        description: `${failedTranslation}, ${processedTranslation}`,
      });
    } else if (hasFailedIccids && !hasProcessedIccids) {
      errorToast({
        title: t('sim.autoReload.failedTitle'),
        description: failedTranslation,
      });
    }
  }, [
    enabled,
    errorToast,
    mutateAsync,
    queryClient,
    setRedirectUrl,
    simCards,
    successToast,
    t,
    warningToast,
  ]);

  return (
    <HStack {...rest}>
      <FormLabel htmlFor="auto-reload-switch" mb="0">
        {t('sim.autoReload.label')}
      </FormLabel>
      <Box boxSize="8">
        {isPending ? (
          <Spinner size="md" />
        ) : (
          <Switch
            id="auto-reload-switch"
            size="md"
            isChecked={enabled}
            onChange={handleToggle}
            isDisabled={isPending}
          />
        )}
      </Box>
    </HStack>
  );
};
